import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import { Formik, Form, getIn } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../redux/modules/userSlice";
import { DownloadFormHtmlToPdf, NdaAPI } from "../components/api";
import useDraft from "../hooks/useDraft";
import {
  applySectionDraftData,
  pickSectionDraftData,
} from "../drafts/draftHelpers";
import {
  JOINEE_FORM_COLORS,
  JOINEE_FORM_FONT,
} from "../config/joineeFormConstants";
import JoineeFormShell, {
  JoineeFormFooterActions,
} from "../components/NewJoineForm/JoineeFormShell";
import {
  FormSectionTitle,
  FormCheckboxField,
} from "../components/NewJoineForm/JoineeFormFields";
import ApprovedCustomizedDialogs from "../components/Model/ModelApproved";
import {
  canShowHrApprove,
  canShowHrResubmit,
  canShowHrReviewActions,
  isJoineeEmployeeFormLocked,
} from "../config/joineeConstants";
import {
  resolveCompanyName,
  resolveCompanyAddress,
} from "../config/companyConfig";
import { preferSavedDate } from "../utils/dateHelpers";
import ReSubmitCustomizedDialogs from "../components/Model/ModelReSubmit";
import LoadingButtons from "../components/Model/LoadingButtonModel";
import { notifyFormInvalid } from "../utils/inputRules";
import HrResubmitNotice from "../components/Joinee/HrResubmitNotice";

var EMPTY_DISPLAY = "—";
var CONSENT_LABEL =
  "I confirm that the information provided is true and accurate, authorize the use of my previously submitted signature, and agree to all applicable terms and conditions.";

function errMsg(formik, path) {
  var touched = getIn(formik.touched, path);
  var error = getIn(formik.errors, path);
  return touched && error ? error : "";
}

function displayValue(value) {
  if (value === null || value === undefined) {
    return EMPTY_DISPLAY;
  }
  var text = String(value).trim();
  return text ? text : EMPTY_DISPLAY;
}

function formatDob(value) {
  if (!value) {
    return EMPTY_DISPLAY;
  }
  var d = dayjs(value);
  return d.isValid() ? d.format("DD MMM YYYY") : displayValue(value);
}

function formatEffectiveDate(value) {
  if (!value) {
    return EMPTY_DISPLAY;
  }
  var d = dayjs(value);
  return d.isValid() ? d.format("DD/MM/YYYY") : displayValue(value);
}

function Highlight(props) {
  return (
    <Box
      component="span"
      sx={{
        color: JOINEE_FORM_COLORS.primary,
        fontWeight: 600,
      }}
    >
      {props.children}
    </Box>
  );
}

function LegalBox(props) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "stretch",
        alignSelf: "stretch",
        flexGrow: 0,
        isolation: "isolate",
        width: "100%",
        padding: "12px 6px",
        gap: "10px",
        bgcolor: "#F1F2F4",
        borderRadius: "4px",
        boxSizing: "border-box",
        mb: "20px",
        mt: 0,
      }}
    >
      {props.children}
    </Box>
  );
}

function LegalHeading(props) {
  return (
    <Typography
      sx={{
        fontFamily: JOINEE_FORM_FONT,
        fontSize: props.small ? 16 : 18,
        fontWeight: 700,
        lineHeight: props.small ? "24px" : "26px",
        color: "#171A1C",
        m: 0,
        width: "100%",
        textAlign: "left",
        px: "10px",
        boxSizing: "border-box",
      }}
    >
      {props.children}
    </Typography>
  );
}

function LegalPara(props) {
  return (
    <Typography
      component={props.component || "p"}
      sx={{
        fontFamily: JOINEE_FORM_FONT,
        fontSize: 14,
        fontWeight: 400,
        color: "#2E3338",
        lineHeight: "22px",
        m: 0,
        width: "100%",
        alignSelf: "stretch",
        px: "10px",
        boxSizing: "border-box",
      }}
    >
      {props.children}
    </Typography>
  );
}

function ReadOnlyRow(props) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: "16px", sm: "32px" },
        width: "100%",
        mb: 2,
      }}
    >
      {(props.items || []).map(function (item, index) {
        return (
          <Box
            key={item.label + "-" + index}
            sx={{
              display: "flex",
              flex: "1 1 0",
              gap: "16px",
              alignItems: "flex-start",
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontFamily: JOINEE_FORM_FONT,
                fontSize: 14,
                fontWeight: 400,
                color: "#848F9A",
                lineHeight: 1.14,
                width: { xs: "110px", sm: "120px" },
                flexShrink: 0,
              }}
            >
              {item.label}
            </Typography>
            <Typography
              sx={{
                fontFamily: JOINEE_FORM_FONT,
                fontSize: 14,
                fontWeight: 400,
                color: "#171A1C",
                lineHeight: 1.14,
                minWidth: 0,
                wordBreak: "break-word",
              }}
            >
              {item.value}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function buildValidationSchema(isAdmin) {
  if (isAdmin) {
    return Yup.object({});
  }
  return Yup.object({
    ndaLegalConsent: Yup.boolean().oneOf(
      [true],
      "Please confirm that the information provided is true and accurate"
    ),
  });
}

function Nda(props) {
  var navigate = useNavigate();
  var params = useParams();
  var routeId = params.id ? params.id : "";
  var redux = useSelector(selectUser);
  var user = redux && redux.user ? redux.user : {};
  var isAdmin = Boolean(user.isAdmin);
  var userId = isAdmin ? props.id || routeId : user._id;

  var draftApi = useDraft("nda", "main");
  var loadDraft = draftApi.loadDraft;
  var saveDraft = draftApi.saveDraft;
  var deleteDraft = draftApi.deleteDraft;

  var [loading, setLoading] = useState(true);
  var [busy, setBusy] = useState(false);
  var [formKey, setFormKey] = useState(0);
  var [initialValues, setInitialValues] = useState(null);
  var [ndaFlagButton, setNdaFlagButton] = useState("");
  var [approvedComment, setApprovedComment] = useState("");
  var [reSubmitComment, setReSubmitComment] = useState("");
  var [previewOpen, setPreviewOpen] = useState(false);
  var [previewHtml, setPreviewHtml] = useState("");
  var [previewLoading, setPreviewLoading] = useState(false);

  useEffect(
    function () {
      var isActive = true;
      if (!userId) {
        setLoading(false);
        return function () {
          isActive = false;
        };
      }

      setLoading(true);
      NdaAPI.getNda(userId)
        .then(function (res) {
          if (!isActive) {
            return;
          }
          var data = res && res.data ? res.data : {};
          var userResp = data.userResp || {};
          var ndaData = data.ndaData || {};
          var sdf = data.selfDeclarationResp || {};
          var todayIso = new Date().toISOString();
          var notes = userResp.internal_notes || {};

          var obj = {
            NdaId: ndaData._id || "",
            effectiveDate: userResp.doj || ndaData.effectiveDate || null,
            reciepient: userResp.name || "",
            Place: sdf.place || ndaData.Place || "",
            date: preferSavedDate(ndaData.date, todayIso),
            name: userResp.name || "",
            companyName: userResp.companyName || "",
            ndaLegalConsent: Boolean(ndaData.ndaLegalConsent),
            ndaLegalConsentAt: ndaData.ndaLegalConsentAt || null,
          };

          setNdaFlagButton(userResp.isNDAFormComplete || "");
          setApprovedComment(notes.NDAFormComment || "");
          setReSubmitComment(userResp.NDAFormComment || "");

          if (isAdmin) {
            setInitialValues(obj);
            setFormKey(function (k) {
              return k + 1;
            });
            setLoading(false);
            return;
          }

          return loadDraft().then(function (draft) {
            if (!isActive) {
              return;
            }
            var merged = applySectionDraftData("nda", "main", obj, draft);
            merged.effectiveDate = obj.effectiveDate;
            merged.reciepient = obj.reciepient;
            merged.Place = obj.Place;
            merged.date = preferSavedDate(
              merged.date || ndaData.date,
              todayIso
            );
            merged.name = obj.name;
            merged.companyName = obj.companyName;
            setInitialValues(merged);
            setFormKey(function (k) {
              return k + 1;
            });
            setLoading(false);
          });
        })
        .catch(function (err) {
          console.log(err);
          if (isActive) {
            setLoading(false);
            toast.error("Unable to load NDA");
          }
        });

      return function () {
        isActive = false;
      };
    },
    [userId, isAdmin, loadDraft]
  );

  var showCandidateActions =
    !isAdmin && !isJoineeEmployeeFormLocked(ndaFlagButton);

  function payloadFromValues(values) {
    var accepted = Boolean(values.ndaLegalConsent);
    return {
      effectiveDate: values.effectiveDate || null,
      reciepient: values.reciepient || "",
      Place: values.Place || "",
      date: preferSavedDate(values.date, new Date().toISOString()),
      ndaLegalConsent: accepted,
      ndaLegalConsentAt: accepted
        ? values.ndaLegalConsentAt || new Date().toISOString()
        : null,
    };
  }

  function persistNda(values) {
    var payload = payloadFromValues(values);
    if (values.NdaId) {
      return NdaAPI.updateNda(userId, payload).then(function (res) {
        return { res: res, NdaId: values.NdaId };
      });
    }
    return NdaAPI.createNda(userId, payload).then(function (res) {
      var newId =
        res && res.data && res.data._id ? res.data._id : values.NdaId;
      return { res: res, NdaId: newId };
    });
  }

  function handleSaveDraft(formik) {
    if (isAdmin) {
      return Promise.resolve();
    }
    var wrapped = pickSectionDraftData("nda", "main", formik.values);
    setBusy("draft");
    return saveDraft(wrapped)
      .then(function (saved) {
        setBusy(false);
        if (saved && saved.skipped) {
          toast.info("Nothing to save as draft");
          return saved;
        }
        if (saved) {
          toast.success("Draft saved");
          return saved;
        }
        toast.error("Unable to save draft");
        return null;
      })
      .catch(function () {
        setBusy(false);
        toast.error("Unable to save draft");
        return null;
      });
  }

  function handleSaveAndNext(values, formikHelpers) {
    if (isAdmin) {
      if (formikHelpers && formikHelpers.setSubmitting) {
        formikHelpers.setSubmitting(false);
      }
      return;
    }
    setBusy("next");
    persistNda(values)
      .then(function (result) {
        if (result && result.NdaId && !values.NdaId) {
          formikHelpers.setFieldValue("NdaId", result.NdaId);
        }
        if (
          values.ndaLegalConsent &&
          !values.ndaLegalConsentAt &&
          formikHelpers.setFieldValue
        ) {
          formikHelpers.setFieldValue(
            "ndaLegalConsentAt",
            payloadFromValues(values).ndaLegalConsentAt
          );
        }
        return deleteDraft().then(function () {
          return NdaAPI.NdaStatus(userId);
        });
      })
      .then(function () {
        setBusy(false);
        if (formikHelpers && formikHelpers.setSubmitting) {
          formikHelpers.setSubmitting(false);
        }
        setNdaFlagButton("submitted");
        toast.success("NDA submitted");
        navigate("/");
      })
      .catch(function (err) {
        console.log(err);
        setBusy(false);
        if (formikHelpers && formikHelpers.setSubmitting) {
          formikHelpers.setSubmitting(false);
        }
        toast.error("Unable to save NDA");
      });
  }

  function openPreview() {
    if (!userId) {
      return;
    }
    setPreviewLoading(true);
    DownloadFormHtmlToPdf.getform(userId, "NdaFormHtmlData")
      .then(function (res) {
        setPreviewLoading(false);
        if (res && res.success) {
          setPreviewHtml(res.data || "");
          setPreviewOpen(true);
        } else {
          toast.error("Unable to load preview");
        }
      })
      .catch(function () {
        setPreviewLoading(false);
        toast.error("Unable to load preview");
      });
  }

  var formApprovedProps = {
    userId: props.id || routeId,
    formState: "isNDAFormComplete",
    nextURL: "AdminEmployees",
    approvedComment: approvedComment,
    setApprovedComment: setApprovedComment,
  };

  var formReSubmitProps = {
    userId: props.id || routeId,
    formState: "isNDAFormComplete",
    nextURL: "AdminEmployees",
    reSubmitComment: reSubmitComment,
    setReSubmitComment: setReSubmitComment,
  };

  if (loading || !initialValues) {
    return (
      <Box sx={{ width: "100%", py: 6, textAlign: "center" }}>
        <Typography
          sx={{
            fontFamily: JOINEE_FORM_FONT,
            fontSize: 14,
            color: JOINEE_FORM_COLORS.label,
          }}
        >
          Loading NDA…
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", pb: 2 }}>
      {isAdmin && !props.hideHrActions && canShowHrReviewActions(ndaFlagButton) ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            mb: 1.5,
            width: { xs: "100%", md: "82%" },
            maxWidth: 780,
            mx: "auto",
          }}
        >
          {canShowHrApprove(ndaFlagButton) ? (
            <ApprovedCustomizedDialogs formApprovedProps={formApprovedProps} />
          ) : null}
          {canShowHrResubmit(ndaFlagButton) ? (
            <ReSubmitCustomizedDialogs formReSubmitProps={formReSubmitProps} />
          ) : null}
        </Box>
      ) : null}

      <Formik
        key={formKey}
        initialValues={initialValues}
        enableReinitialize
        validationSchema={buildValidationSchema(isAdmin)}
        onSubmit={handleSaveAndNext}
      >
        {function (formik) {
          var values = formik.values;

          return (
            <Form noValidate>
              <JoineeFormShell
                banner={
                  !isAdmin && ndaFlagButton === "resubmission required" ? (
                    <HrResubmitNotice comment={reSubmitComment} embedded />
                  ) : null
                }
                hideSteps
                onBack={
                  !isAdmin
                    ? function () {
                        navigate("/");
                      }
                    : null
                }
                title="Non-disclosure Agreement"
                subtitle="Review the confidentiality terms and accept the agreement as part of your employment onboarding"
                headerAction={
                  isAdmin ? (
                    previewLoading ? (
                      <LoadingButtons />
                    ) : (
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={openPreview}
                        sx={{
                          fontFamily: JOINEE_FORM_FONT,
                          fontSize: 14,
                          fontWeight: 400,
                          textTransform: "none",
                          color: JOINEE_FORM_COLORS.primary,
                          borderColor: JOINEE_FORM_COLORS.primary,
                          borderRadius: "6px",
                          minHeight: 32,
                          px: "12px",
                          bgcolor: "#FDFDFD",
                        }}
                      >
                        Preview Form
                      </Button>
                    )
                  ) : null
                }
                footer={
                  showCandidateActions ? (
                    <JoineeFormFooterActions
                      showPrevious
                      onPrevious={function () {
                        navigate("/");
                      }}
                      onDraft={function () {
                        return handleSaveDraft(formik);
                      }}
                      onNext={function () {
                        formik.validateForm().then(function (errors) {
                          if (errors && Object.keys(errors).length) {
                            notifyFormInvalid(errors);
                            formik.setTouched(
                              { ndaLegalConsent: true },
                              true
                            );
                            return;
                          }
                          formik.handleSubmit();
                        });
                      }}
                      nextLabel="Save and next"
                      disabled={Boolean(busy)}
                      drafting={busy === "draft"}
                      saving={busy === "next"}
                    />
                  ) : null
                }
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    padding: 0,
                    gap: "20px",
                    isolation: "isolate",
                    width: "100%",
                    alignSelf: "stretch",
                    flexGrow: 0,
                    mb: "20px",
                  }}
                >
                  <LegalBox>
                    <LegalPara>
                      This Nondisclosure Agreement (this “Agreement”), effective{" "}
                      <Highlight>
                        {formatEffectiveDate(values.effectiveDate)}
                      </Highlight>
                      , is entered into by and between{" "}
                      {resolveCompanyName(values.companyName)}, having offices
                      at {resolveCompanyAddress(values.companyName)} (“Company”)
                      and <Highlight>{displayValue(values.reciepient)}</Highlight>
                      , (each herein referred to individually as a “Party” or
                      collectively as the “Parties”). In consideration of the
                      covenants and conditions contained herein, the Parties
                      hereby agree to the following:
                    </LegalPara>
                  </LegalBox>
                </Box>

                <FormSectionTitle>PURPOSE</FormSectionTitle>
                <LegalBox>
                  <LegalPara>
                    The Parties wish to explore a business opportunity of mutual
                    interest and/or, consulting services (the “Opportunity”), and
                    in connection with the Opportunity, Company has disclosed,
                    and may further disclose to the Recipient certain
                    confidential technical and business information that Company
                    desires Recipient to treat as confidential.
                  </LegalPara>
                </LegalBox>

                <LegalBox>
                  <LegalHeading>CONFIDENTIAL INFORMATION</LegalHeading>
                  <Box sx={{ width: "100%" }}>
                    <LegalHeading small>1) Definition :</LegalHeading>
                    <LegalPara>
                      &quot;Confidential Information&quot; means any information
                      (including any and all combinations of individual items of
                      information) disclosed by the Company to the Recipient,
                      including any information disclosed prior to the Effective
                      Date, either directly or indirectly in writing, orally, or
                      by inspection of tangible objects (including, without
                      limitation, research, product plans, products, services,
                      equipment, customers, markets, software, inventions,
                      discoveries, ideas, processes, designs, drawings, hardware,
                      formulations, specifications, product configuration
                      information, marketing and finance documents, prototypes,
                      samples, data sets, and equipment), whether or not
                      designated as &quot;confidential&quot; at the time of
                      disclosure. Confidential Information may also include
                      information of a third party that is in the
                      Company&apos;s possession and is disclosed to the Recipient
                      under this Agreement.
                    </LegalPara>
                  </Box>
                  <Box sx={{ width: "100%" }}>
                    <LegalHeading small>2) Exceptions :</LegalHeading>
                    <LegalPara>
                      Confidential Information shall not, however, include any
                      information that Recipient can establish:
                      <br />
                      (i) was publicly known or made generally available without
                      a duty of confidentiality prior to the time of disclosure
                      to Recipient by Company.
                      <br />
                      (ii) becomes publicly known or made generally available
                      without a duty of confidentiality after disclosure to
                      Recipient by Company through no action or inaction of
                      Recipient.
                      <br />
                      (iii) is in the rightful possession of Recipient without
                      confidentiality obligations at the time of disclosure by
                      Company to Recipient as shown by Recipient&apos;s
                      then-contemporaneous written files and records kept in.
                    </LegalPara>
                  </Box>
                  <Box sx={{ width: "100%" }}>
                    <LegalHeading small>3) Compelled Disclosure :</LegalHeading>
                    <LegalPara>
                      If Recipient becomes legally compelled to disclose any
                      Confidential Information, other than pursuant to a
                      confidentiality agreement, the ordinary course of business;
                      provided that any combination of individual items of
                      information shall not be deemed to be within any of the
                      foregoing exceptions merely because one or more of the
                      individual items are within such exception unless the
                      combination as a whole is within such exception Recipient
                      will provide Company prompt written notice of such
                      disclosure and will assist Company is seeking a protective
                      order or another appropriate remedy. If Company waives
                      Recipient&apos;s compliance with this Agreement or fails to
                      obtain a protective order or other appropriate remedies,
                      Recipient will furnish only that portion of the
                      Confidential Information that is legally required to be
                      disclosed, provided that any Confidential Information so
                      disclosed shall maintain its confidentiality protection for
                      all purposes other than such legally compelled disclosure.
                    </LegalPara>
                  </Box>
                </LegalBox>

                <FormSectionTitle>NON-USE AND NON-DISCLOSURE</FormSectionTitle>
                <LegalBox>
                  <LegalPara mb={0}>
                    The recipient shall not use any Confidential Information for
                    any purpose except to evaluate and engage in discussions
                    concerning the Opportunity. Recipient shall not disclose any
                    Confidential Information or permit any Confidential
                    Information to be disclosed, either directly or indirectly,
                    to any third party without Company&apos;s prior written
                    consent. Recipient shall not disclose Confidential
                    Information or permit the disclosure of Confidential
                    Information to its employees, except that Recipient may
                    disclose Confidential Information to those employees of
                    Recipient who are required to have the information in order
                    for Recipient to evaluate or engage in discussions concerning
                    the Opportunity; provided that such employee has signed a
                    non-use and non-disclosure agreement in content at least as
                    protective as the provisions hereof, prior to any disclosure
                    of Confidential Information to such employee. The recipient
                    shall not reverse engineer, disassemble, or decompile any
                    prototypes, software, samples, or other tangible objects that
                    embody the Confidential Information. &quot;Recipient shall
                    not file any patent application(s) containing or based on, in
                    whole or in part, any of Company&apos;s Confidential
                    Information and/or relating to the GNSS applications.&quot;
                  </LegalPara>
                </LegalBox>

                <FormSectionTitle>MAINTENANCE OF CONFIDENTIALITY</FormSectionTitle>
                <LegalBox>
                  <LegalPara mb={0}>
                    The recipient shall take reasonable measures to protect the
                    secrecy of and avoid disclosure and unauthorized use of the
                    Confidential Information. Without limiting the foregoing,
                    Recipient shall take at least those measures it employs to
                    protect its own most highly confidential information.
                    Recipient shall not make any copies of the Confidential
                    Information unless the same are previously approved in
                    writing by Company. Recipient shall reproduce Company&apos;s
                    proprietary rights notices on any such authorized copies, in
                    the same manner in which such notices were set forth in or on
                    the original. Recipient shall immediately notify Company of
                    any unauthorized use or disclosure, or suspected unauthorized
                    use or disclosure, of Confidential Information.
                  </LegalPara>
                </LegalBox>

                <FormSectionTitle>NO OBLIGATION</FormSectionTitle>
                <LegalBox>
                  <LegalPara mb={0}>
                    Nothing in this Agreement shall obligate either Party to
                    proceed with any transaction between them, and each Party
                    reserves the right, in its sole discretion, to terminate the
                    discussions contemplated by this Agreement concerning the
                    Opportunity. Nothing in this Agreement shall be construed to
                    restrict the Company&apos;s use or disclosure of its own
                    Confidential Information.
                  </LegalPara>
                </LegalBox>

                <FormSectionTitle>NO WARRANTY</FormSectionTitle>
                <LegalBox>
                  <LegalPara mb={0}>
                    All Confidential Information Is Provided &quot;As-Is.&quot;
                    Company Makes No Warranties, Express, Implied Or Otherwise,
                    Regarding The Accuracy, Completeness Or Performance Of Any
                    Confidential Information, Or With Respect To Non-Infringement
                    Or Other Violation Of Any Intellectual Property Rights Of A
                    Third Party Or Of Recipient.
                  </LegalPara>
                </LegalBox>

                <FormSectionTitle>RETURN OF MATERIALS</FormSectionTitle>
                <LegalBox>
                  <LegalPara mb={0}>
                    All documents and other tangible objects containing or
                    representing Confidential Information and all copies or
                    extracts thereof or notes derived therefrom that are in the
                    possession or control of Recipient shall be and remain the
                    property of Company and shall be promptly returned to Company
                    or destroyed (with proof of such destruction), each upon
                    Company&apos;s request.
                  </LegalPara>
                </LegalBox>

                <FormSectionTitle>NO LICENSE</FormSectionTitle>
                <LegalBox>
                  <LegalPara mb={0}>
                    Nothing in this Agreement is intended to grant any rights to
                    Recipient under any intellectual property right of Company,
                    nor shall this Agreement grant Recipient any rights in or to
                    the Confidential Information except as expressly set forth in
                    this Agreement.
                  </LegalPara>
                </LegalBox>

                <FormSectionTitle>TERMS</FormSectionTitle>
                <LegalBox>
                  <LegalPara mb={0}>
                    The obligations of Recipient under this Agreement shall
                    survive until such time as all Confidential Information
                    disclosed hereunder becomes publicly known or made generally
                    available through no action or inaction of Recipient.
                  </LegalPara>
                </LegalBox>

                <FormSectionTitle>REMEDIES</FormSectionTitle>
                <LegalBox>
                  <LegalPara mb={0}>
                    Recipient agrees that any violation or threatened violation
                    of this Agreement will cause irreparable injury to Company,
                    entitling Company to obtain injunctive relief in addition to
                    all legal remedies without showing or proving any actual
                    damage and without any bond being required to be posted.
                  </LegalPara>
                </LegalBox>

                <FormSectionTitle>RECIPIENT INFORMATION</FormSectionTitle>
                <LegalBox>
                  <LegalPara>
                    The company does not wish to receive any confidential
                    information from the Recipient, and Company assumes no
                    obligation, either expressed or implied, with respect to any
                    information disclosed by the Recipient to Company.
                  </LegalPara>
                  <LegalPara mb={0}>
                    Any ideas, suggestions, guidance or other information
                    disclosed by the Recipient related to GNSS Technology and any
                    intellectual property rights relating to the foregoing shall
                    be collectively deemed &quot;Feedback.&quot; Company shall
                    own all Feedback, and Recipient agrees to assign and hereby
                    assigns to Company all of its right, title, and interest in
                    and to such Feedback. To the extent that the foregoing
                    assignment is ineffective for whatever reason, Recipient
                    agrees to grant and hereby grants to Company a non-exclusive,
                    perpetual, irrevocable, royalty-free, worldwide license (with
                    the right to grant and authorize sublicenses) to make, have
                    made, use, import, offer for sale, sell, reproduce,
                    distribute, modify, adapt, prepare derivative works of,
                    display, perform and otherwise exploit such Feedback without
                    restriction.
                  </LegalPara>
                </LegalBox>

                <FormSectionTitle>MISCELLANEOUS</FormSectionTitle>
                <LegalBox>
                  <LegalPara mb={0}>
                    This Agreement shall bind and inure to the benefit of the
                    Parties and their respective successors and permitted
                    assigns; except that Recipient may not assign or otherwise
                    transfer this Agreement, by operation of law or otherwise,
                    (including by merger, reorganization, consolidation, change
                    of control, or sale of all or substantially all of
                    Recipient&apos;s assets to which this Agreement pertains),
                    without the written consent of Company. Any assignment or
                    transfer of this Agreement in violation of the foregoing
                    shall be null and void. This Agreement will be interpreted
                    and construed in accordance with the laws of the State of
                    California, without regard to conflict of law principles.
                    Recipient hereby represents and warrants that the persons
                    executing this Agreement on its behalf have express authority
                    to do so, and, in so doing, to bind Recipient thereto. This
                    Agreement contains the entire agreement between the Parties
                    with respect to the Opportunity and supersedes all prior
                    written and oral agreements between the Parties. If a court
                    or other body of competent jurisdiction finds any provision
                    of this Agreement, or portion thereof, to be invalid or
                    unenforceable, such provision will be enforced to the maximum
                    extent permissible so as to effect the intent of the Parties,
                    and the remainder of this Agreement will continue in full
                    force and effect. No provision of this Agreement may be
                    waived except by a writing executed by the Party against whom
                    the waiver is to be effective. A Party&apos;s failure to
                    enforce any provision of this Agreement shall neither be
                    construed as a waiver of the provision nor prevent the Party
                    from enforcing any other provision of this Agreement. No
                    provision of this Agreement may be amended or otherwise
                    modified except by a writing signed by the Parties to this
                    Agreement. The Parties may execute this Agreement in
                    counterparts, each of which shall be deemed an original, but
                    all of which together constitute one and the same agreement.
                    This Agreement may be delivered by facsimile transmission,
                    and facsimile copies of executed signature pages shall be
                    binding as originals.
                  </LegalPara>
                </LegalBox>

                <FormSectionTitle>Authorization</FormSectionTitle>
                <ReadOnlyRow
  items={[
    {
      label: "Full Name:",
      value: displayValue(values.name),
    },
    {
      label: "Place:",
      value: displayValue(values.Place),
    },
  ]}
/>

<ReadOnlyRow
  items={[
    {
      label: "Date:",
      value: formatDob(values.date),
    },
  ]}
/>

                {!isAdmin ? (
                  <Box
                    sx={{ mt: 1, mb: 1 }}
                    data-ack-error={
                      errMsg(formik, "ndaLegalConsent") ? "true" : undefined
                    }
                  >
                    <FormCheckboxField
                      name="ndaLegalConsent"
                      checked={Boolean(values.ndaLegalConsent)}
                      onChange={function (e) {
                        var checked = Boolean(e.target.checked);
                        formik.setFieldValue("ndaLegalConsent", checked, true);
                        formik.setFieldValue(
                          "ndaLegalConsentAt",
                          checked
                            ? values.ndaLegalConsentAt ||
                                new Date().toISOString()
                            : null,
                          false
                        );
                        formik.setFieldTouched(
                          "ndaLegalConsent",
                          true,
                          false
                        );
                        if (checked) {
                          formik.setFieldError("ndaLegalConsent", undefined);
                        }
                      }}
                      label={CONSENT_LABEL}
                      disabled={!showCandidateActions}
                      legal
                    />
                    {errMsg(formik, "ndaLegalConsent") &&
                    !values.ndaLegalConsent ? (
                      <Typography
                        sx={{
                          fontFamily: JOINEE_FORM_FONT,
                          fontSize: 12,
                          fontWeight: 400,
                          color: JOINEE_FORM_COLORS.error,
                          mt: 0.5,
                          ml: "28px",
                        }}
                      >
                        {errMsg(formik, "ndaLegalConsent")}
                      </Typography>
                    ) : null}
                    <Box
                      sx={{
                        borderBottom:
                          "1px solid " + JOINEE_FORM_COLORS.border,
                        mt: 2,
                        mb: 1,
                      }}
                    />
                  </Box>
                ) : null}
              </JoineeFormShell>
            </Form>
          );
        }}
      </Formik>

      <Dialog
        open={previewOpen}
        onClose={function () {
          setPreviewOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontFamily: JOINEE_FORM_FONT,
            fontWeight: 600,
            pr: 6,
          }}
        >
          Preview Form
          <IconButton
            aria-label="close"
            onClick={function () {
              setPreviewOpen(false);
            }}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{ width: "100%", overflowX: "auto" }}
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default Nda;
